import { AttachFile, Delete } from "@mui/icons-material";
import { Alert, Box, Button, Divider, Grid, IconButton, InputLabel, Typography } from "@mui/material";
import React, { useCallback, useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Slider from "react-slick";
import ClaimApiService from "../../adapters/xhr/ClaimApiService";
import { MyButton } from "../../components/my-button/MyButton";
import { MyCheckbox } from "../../components/my-checkbox/MyCheckbox";
import { getConfirmation } from "../../components/my-confirmation-dialog/MyConfirmationDialog";
import { MyDatePicker } from "../../components/my-date-picker/MyDatePicker";
import { MyTextField } from "../../components/my-text-field/MyTextField";
import { AllowedFileExtensions, EmailRegExp, lngs, MaxReceiptSize, PhoneNumberRegExp, SliderSettings, ValidTextRegExp } from "../../Constant";
import MyContext from "../../contexts/MyContext";
import { TrainTheme } from "../../infrastructure/TrainThemeProvider";
import Logosvg from "../../static/malartag_favicon_blue.svg";
import Logopng from "../../static/malartag_favicon_blue_png.png";
import { formatDate, toBase64 } from "../../Utils";

export const TrainSurchargePage = (props) => {
  const MaxNumberOfAttachments = 5;
  const { t, i18n } = useTranslation();
  let sliderRef = React.createRef();
  const myContext = useContext(MyContext);
  const [caseNumber, setCaseNumber] = useState("");
  const [controlNumber, setControlNumber] = useState("");
  const [firstName, setFirstName] = useState("");
  const [surName, setSurName] = useState("");
  const [email, setEmail] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [ticketNumber, setTicketNumber] = useState("");
  const [comment, setComment] = useState("");
  const [surchargeDate, setSurchargeDate] = useState(null);
  const [agreePersonalDataProcessing, setAgreePersonalDataProcessing] = useState(false);
  const [attachments, setAttachments] = useState([]);

  const [error, setError] = useState(null);
  const [controlNumberErr, setControlNumberErr] = useState("");
  const [firstNameErr, setFirstNameErr] = useState("");
  const [surNameErr, setSurNameErr] = useState("");
  const [emailErr, setEmailErr] = useState("");
  const [phoneNumberErr, setPhoneNumberErr] = useState("");
  const [commentErr, setCommentErr] = useState("");
  const [ticketNumberErr, setTicketNumberErr] = useState("");
  const [surchargeDateValueErr, setSurchargeDateValueErr] = useState("");
  const [agreePersonalDataProcessingErr, setAgreePersonalDataProcessingErr] = useState("");

  const hiddenFileInput = React.useRef(null);

  const handleClick = (event) => {
    hiddenFileInput.current.click();
  };

  const removeAttachment = (fileName) => {
    setAttachments(attachments.filter((item) => item.name !== fileName));
  };

  const getAllowedFileExtension = useCallback(async () => {
    let response = await ClaimApiService.getAllowedFileExtension();
    if (response.isSuccess && response.data) myContext.changeAllowFileUploadExtension(response.data);
    else myContext.changeAllowFileUploadExtension(AllowedFileExtensions.map((item) => item.key));
  }, []);

  useEffect(() => {
    if (!myContext.allowFileUploadExtension || myContext.allowFileUploadExtension.length === 0) getAllowedFileExtension();
  }, []);

  const handleChangeAttachment = (event) => {
    const fileUploaded = event.target.files[0];
    if (myContext.allowFileUploadExtension.find((extension) => fileUploaded.name.toLowerCase().endsWith(extension.toLowerCase())) == null) setError("not_allowed");
    else if (fileUploaded.size > MaxReceiptSize) setError("your_uploaded_file_too_big");
    else {
      setAttachments((oldArray) => [...oldArray.filter((item) => item.name !== fileUploaded.name), fileUploaded]);
      setError(null);
    }
  };

  useEffect(() => {
    document.title = "Mälardalstrafik - Bestridan av tilläggsavgift";
    // append link tag to document head
    document.head.appendChild(Object.assign(document.createElement("link"), { rel: "icon", type: "image/png", href: Logopng }));
    document.head.appendChild(Object.assign(document.createElement("link"), { rel: "icon", type: "image/svg+xml", href: Logosvg }));
  }, []);

  useEffect(() => {
    sliderRef.slickGoTo(myContext.activeSlide, false);
    window.scrollTo({ top: 0, behavior: "auto" });
  }, [myContext.activeSlide]);

  const validate = async () => {
    let isValid = true;

    if (!comment.trim()) {
      setCommentErr("required");
      isValid = false;
    } else {
      setCommentErr("");
    }

    if (!firstName.trim()) {
      setFirstNameErr("required");
      isValid = false;
    } else {
      setFirstNameErr("");
    }

    if (!surName.trim()) {
      setSurNameErr("required");
      isValid = false;
    } else {
      setSurNameErr("");
    }

    if (!surchargeDate) {
      setSurchargeDateValueErr("required");
      isValid = false;
    } else {
      setSurchargeDateValueErr("");
    }

    if (phoneNumber.trim()) {
      if (!new RegExp(PhoneNumberRegExp).test(phoneNumber.trim())) {
        setPhoneNumberErr("invalid");
        isValid = false;
      } else setPhoneNumberErr("");
    } else setPhoneNumberErr("");

    if (!email.trim()) {
      setEmailErr("required");
      isValid = false;
    } else if (!new RegExp(EmailRegExp).test(email.trim())) {
      setEmailErr("invalid");
      isValid = false;
    } else setEmailErr("");

    if (isValid && !agreePersonalDataProcessing) {
      setAgreePersonalDataProcessingErr("agree_personal_data_processing_warning");
      isValid = false;
    } else {
      setAgreePersonalDataProcessingErr("");
    }

    if (isValid) {
      let result = await ClaimApiService.createSurcharge({
        fullName: `${firstName} ${surName}`,
        email: email?.trim(),
        phoneNumber: phoneNumber?.trim(),
        comment: comment?.trim(),
        ticketNumber: ticketNumber?.trim(),
        controlNumber: controlNumber?.trim(),
        surchargeDate: `${formatDate(surchargeDate)}`,
        attachments: await Promise.all(
          attachments.map(async (file) => {
            return {
              file: await toBase64(file),
              fileName: file.name,
            };
          })
        ),
      });
      if (!result?.isSuccess) {
        const confirmed = await getConfirmation({
          title: t("error"),
          message: t("refresh_page_message"),
          theme: TrainTheme,
        });
        if (confirmed) window.location.reload();
      } else {
        setControlNumber("");
        setFirstName("");
        setSurName("");
        setTicketNumber("");
        setPhoneNumber("");
        setEmail("");
        setComment("");
        setAttachments([]);
        setSurchargeDate(null);
        setAgreePersonalDataProcessing(false);
        setError(null);
        setCaseNumber(result.data);
        myContext.changeActiveSlide((myContext.activeSlide += 1));
      }
    }
  };
  return (
    <MyContext.Consumer>
      {(context) => (
        <Grid container className="surcharge page container custom padding top-2 bottom-2 left-0 right-0" justifyContent="center">
          <Grid item sx={{ label: { fontWeight: "bold", color: "#222" } }}>
            <Slider ref={(slider) => (sliderRef = slider)} {...SliderSettings} beforeChange={(_, next) => context.changeActiveSlide(next)}>
              <div hidden={context.activeSlide !== 0} className="padding left-1 right-1">
                <Box component="small" sx={{ a: { color: "primary.main" } }} dangerouslySetInnerHTML={{ __html: t("surcharge_description") }} />
                <InputLabel style={{ marginTop: "1.25rem" }} className="margin bottom-0" htmlFor="controlNumber">{`${t("surcharge_control_number")}`}</InputLabel>
                <Typography variant="caption" component="p" style={{ width: "100%", marginBottom: "0.5rem" }} dangerouslySetInnerHTML={{ __html: t("control_number_desc") }} />
                <MyTextField id="controlNumber" maxLength={50} helperText={t(controlNumberErr)} regExp={ValidTextRegExp} value={controlNumber} onChange={setControlNumber} placeholder={t("surcharge_control_number")} className="margin bottom-1 top-0" />
                <InputLabel className="margin bottom-0" htmlFor="surchargeDate">{`${t("surcharge_date")}*`}</InputLabel>
                <Typography variant="caption" component="p" style={{ width: "100%", marginBottom: "0.5rem" }} dangerouslySetInnerHTML={{ __html: t("surcharge_desc") }} />
                <MyDatePicker id="surchargeDate" className="margin top-0 bottom-1" required={true} error={surchargeDateValueErr !== ""} value={surchargeDate} onAccept={(date) => setSurchargeDate(date?.toString())} helperText={t(surchargeDateValueErr)} maxDate={new Date()} placeholder={t("date_format")} cancelLabel={t("cancel")} okLabel={t("ok")} locale={i18n.language} views={["day"]} />
                <InputLabel className="margin bottom-0" htmlFor="ticketNumber">{`${t("ticket_number")}`}</InputLabel>
                <MyTextField id="ticketNumber" maxLength={50} helperText={t(ticketNumberErr)} regExp={ValidTextRegExp} value={ticketNumber} onChange={setTicketNumber} placeholder={t("ticket_number")} />
                <InputLabel className="margin bottom-0" htmlFor="surchargeComment">{`${t("surcharge_comment")}*`}</InputLabel>
                <MyTextField id="surchargeComment" required={true} helperText={t(commentErr)} regExp={ValidTextRegExp} value={comment} onChange={setComment} placeholder={t("surcharge_comment")} multiline={true} rows={4} />
                <Box component="h2" color="primary.surchargeHeading" className="margin bottom-0" sx={{ fontSize: { xs: "1.4rem", sm: "1.8rem", md: "2.2rem", lg: "2.4rem", xl: "2.25rem" } }}>
                  {t("surcharge_attachments")}
                </Box>
                <p dangerouslySetInnerHTML={{ __html: t("surcharge_attachments_note", { files: context.allowFileUploadExtension.map((extension) => `.${extension}`.toLowerCase()).join(", ") }) }} />
                {error && (
                  <Alert variant="filled" severity="error" className="margin top-1 bottom-1">
                    {t(error)}
                  </Alert>
                )}
                {(!attachments || attachments.length > 0) && (
                  <>
                    <hr className="margin top-0 bottom-0" aria-hidden={true} />
                    {attachments.map((item, key) => (
                      <Grid container key={key} justifyContent="space-between" alignItems={"center"}>
                        <Grid xs={10} sm={10} item color="primary.main" container wrap="nowrap" alignItems={"center"} style={{ overflowX: "hidden" }}>
                          <AttachFile fontSize="small" color="primary.main" style={{ marginRight: "1rem", marginLeft: "0.5rem" }} />
                          <Box whiteSpace={"nowrap"} component="small" color="primary.main">
                            {item.name}
                          </Box>
                        </Grid>
                        <Grid item>
                          <IconButton aria-label={t("delete")} onClick={(e) => removeAttachment(item.name)}>
                            <Delete fontSize="small" />
                          </IconButton>
                        </Grid>
                      </Grid>
                    ))}
                  </>
                )}
                {(!attachments || attachments.length < MaxNumberOfAttachments) && (
                  <>
                    <hr className="margin top-0 bottom-0" aria-hidden={true} />
                    <Button
                      aria-haspopup="dialog"
                      variant="text"
                      className="button secondary w-100 justify-content-start"
                      style={{
                        textTransform: "unset",
                      }}
                      onClick={handleClick}
                    >
                      <AttachFile fontSize="small" color="primary.main" style={{ marginRight: "1rem" }} />
                      <span>{t("add_receipt_or_certificate")}</span>
                    </Button>
                    <input
                      accept={context.allowFileUploadExtension.map((extension) => `.${extension}`.toLowerCase()).join()}
                      type="file"
                      ref={hiddenFileInput}
                      onChange={handleChangeAttachment}
                      onClick={(event) => {
                        event.target.value = null;
                      }}
                      style={{ display: "none" }}
                    />
                  </>
                )}
                <Divider className="margin top-0" />
                <Box component="h2" color="primary.surchargeHeading" sx={{ marginTop: "1rem", marginBottom: "0.35rem", fontSize: { xs: "1.4rem", sm: "1.8rem", md: "2.2rem", lg: "2.4rem", xl: "2.25rem" } }}>
                  {t("surcharge_contact_details")}
                </Box>
                <InputLabel className="margin bottom-0" htmlFor="firstname">{`${t("firstname")}*`}</InputLabel>
                <MyTextField id="firstname" required={true} value={firstName} placeholder={t("firstname")} maxLength={100} helperText={t(firstNameErr)} onChange={setFirstName} regExp={ValidTextRegExp} />
                <InputLabel className="margin bottom-0" htmlFor="surname">{`${t("surname")}*`}</InputLabel>
                <MyTextField id="surname" value={surName} required={true} regExp={ValidTextRegExp} placeholder={t("surname")} maxLength={100} helperText={t(surNameErr)} onChange={setSurName} />
                <InputLabel className="margin bottom-0" htmlFor="phoneNumber">{`${t("mobile_number")}`}</InputLabel>
                <MyTextField id="phoneNumber" value={phoneNumber} regExp={ValidTextRegExp} placeholder={t("mobile_number")} maxLength={15} helperText={t(phoneNumberErr)} onChange={setPhoneNumber} />
                <InputLabel className="margin bottom-0" htmlFor="email">{`${t("email")}*`}</InputLabel>
                <MyTextField style={{ marginBottom: "0" }} id="email" value={email} regExp={ValidTextRegExp} maxLength={100} placeholder={t("email")} required={true} helperText={t(emailErr)} onChange={setEmail} />
                <MyCheckbox helperText={t(agreePersonalDataProcessingErr)} label={t("surcharge_declaration")} checked={agreePersonalDataProcessing} onChange={setAgreePersonalDataProcessing} className="margin top-1" />
                <Typography sx={{ flex: "1 1 100%", a: { color: "primary.main" } }} component="p" dangerouslySetInnerHTML={{ __html: t("surcharge_read_about_how_data_processed") }} />
                <MyButton onClick={validate} text={t("submit")} style={{ float: "right" }} />
              </div>
              <div hidden={context.activeSlide !== 1}>
                <Grid container spacing={3} style={{ paddingLeft: "1rem", paddingRight: "1rem" }}>
                  <Grid item>
                    <div dangerouslySetInnerHTML={{ __html: t("surcharge_confirmation") }} />
                  </Grid>
                </Grid>
              </div>
            </Slider>
          </Grid>
        </Grid>
      )}
    </MyContext.Consumer>
  );
};
